/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React from 'react';
import { placeholderImageUrl } from '../../components/image';
import { IAny, IGeneric } from '../../interfaces';
import SVGImages, { DEFAULT_PLACEHOLDER, LARGE_IMAGE_PLACEHOLDER, SMALL_IMAGE_PLACEHOLDER, TEST_PLACEHOLDER } from '../svg-loader';
import { IEditableFieldProps } from './editable-field';
import { getEditableFieldClassName, isEditorialMode } from './utils';
import msdyn365Commerce from '../..';

export const ImageField = (props: IEditableFieldProps<IGeneric<IAny>>) => {
    const onKeyPress = (event: React.KeyboardEvent<HTMLImageElement>) => {
        // 'enter' key
        if (event.charCode === 13 && props.onChange) {
            event.preventDefault();
            props.onChange(event);
        }
    };

    const editableProps = props.disabled
        ? {}
        : {
              onKeyPress: onKeyPress,
              onDoubleClick: props.onChange,
              onMouseOver: props.onMouseOver,
              onFocus: props.onFocus,
              onBlur: props.onBlur,
              onMouseOut: props.onMouseOut,
              tabIndex: 0
          };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fieldProps: any = {
        ...props.fieldProps,
        editProps: undefined
    };

    // If the image source is the placeholder image, set it's height to 1px
    if (
        props.fieldProps.src === placeholderImageUrl &&
        (!props.fieldProps['data-src'] || props.fieldProps['data-src'] === placeholderImageUrl)
    ) {
        fieldProps.height = '1px';
    }
    if (fieldProps.placeholderConfig && props.requestContext && isEditorialMode(props.requestContext)) {
        const divStyle = {
            display: 'flex',
            backgroundColor: '#F2F2F2',
            alignItems: 'center',
            width: (fieldProps.placeholderConfig.svgConfig && fieldProps.placeholderConfig.svgConfig.width) || '100%',
            height: (fieldProps.placeholderConfig.svgConfig && fieldProps.placeholderConfig.svgConfig.height) || '100%'
        };
        const svgStyle = {
            margin: '0 auto',
            display: 'block'
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const svgPropety: any = {
            style: svgStyle
        };
        switch (fieldProps.placeholderConfig.name) {
            case DEFAULT_PLACEHOLDER:
                return (
                    <div
                        style={divStyle}
                        {...editableProps}
                        className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    >
                        <SVGImages.largePicturePlaceholder {...svgPropety} />
                    </div>
                );
            // TODO:Add more case when we get full assets of placeholder SVGs
            case LARGE_IMAGE_PLACEHOLDER:
                return (
                    <div
                        style={divStyle}
                        {...editableProps}
                        className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    >
                        <SVGImages.largePicturePlaceholder {...svgPropety} />
                    </div>
                );
            case SMALL_IMAGE_PLACEHOLDER:
                return (
                    <div
                        style={divStyle}
                        {...editableProps}
                        className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    >
                        <SVGImages.smallPicturePlaceholder {...svgPropety} />
                    </div>
                );
            case TEST_PLACEHOLDER:
                return (
                    <div
                        style={divStyle}
                        {...editableProps}
                        className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    >
                        <SVGImages.testPlaceholder {...svgPropety} />
                    </div>
                );
            default:
                return (
                    <div
                        style={divStyle}
                        {...editableProps}
                        className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    >
                        <SVGImages.largePicturePlaceholder {...svgPropety} />
                    </div>
                );
        }
    }
    if (msdyn365Commerce.platformSettings?.imageFallbackOptimize) {
        return (
            <div>
                <img
                    {...fieldProps}
                    {...editableProps}
                    className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                    {...(props.fieldProps.additionalProperties || {})}
                />
            </div>
        );
    } else {
        return (
            <img
                {...fieldProps}
                {...editableProps}
                className={getEditableFieldClassName(props.disabled, props.fieldProps.className, props.requestContext)}
                {...(props.fieldProps.additionalProperties || {})}
            />
        );
    }
};
